import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InputNumber } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { SbButton, SbNotification, SbSwitch, SbSpin } from "uiCore";
import Permission from "Components/Permission";
import useTalentMobilitySettingsStore, {
  getStoredThresholdsSnapshot,
  toThresholdsPatch,
} from "Store/useTalentMobilitySettingsStore";
import { getTalentMobilityThresholds, updateTalentMobilityThresholds } from "Api/apiService";
import {
  PageWrapperStyled,
  HeaderRowStyled,
  BackButtonStyled,
  SettingsCardStyled,
  SectionHeadingStyled,
  FieldRowStyled,
} from "./TalentMobilitySettings.styles";

/**
 * Talent Mobility — Match & Succession Thresholds
 *
 * Admin-configurable thresholds referenced across every Talent Mobility
 * screen via Utils/scoreColor.utils.js + Store/useTalentMobilitySettingsStore.js
 * (req #8: green/amber/red match banding) and Utils/successionScore.utils.js
 * (Engine D: succession plan quality score, req #12+). Two headed sections —
 * "Match Thresholds" (green/red) and "Succession Threshold" (Ready Now +
 * Ready in 1 Year) — since these are two conceptually distinct scoring
 * systems that happen to share this settings page.
 *
 * This is the only screen that talks to the real thresholds API
 * (GET/PUT /api/talent-mobility/thresholds). Every other Talent Mobility
 * screen still reads thresholds from useTalentMobilitySettingsStore via
 * useMatchThresholds/useSuccessionThresholds, so both the initial load and
 * every save here also write through to that store — keeping the rest of
 * the module in sync without each of those screens needing its own fetch.
 */

// Loading and saving are mutually exclusive phases of the same request
// lifecycle, so one status value drives the UI instead of two booleans.
const ThresholdsStatus = Object.freeze({
  LOADING: "loading",
  SAVING: "saving",
  IDLE: "idle",
});

const TalentMobilitySettings = () => {
  const navigate = useNavigate();
  const setStoredThresholds = useTalentMobilitySettingsStore((state) => state.setThresholds);
  const setStoredNotificationsEnabled = useTalentMobilitySettingsStore(
    (state) => state.setMatchNotificationsEnabled
  );

  // One draft object for the whole form, seeded from the store's last-known
  // values as a placeholder until the fetch below resolves.
  // is_open_role_visible is named in API (snake_case) form because the draft
  // is sent to PUT /talent-mobility/thresholds as-is; it defaults to visible
  // until the fetch supplies the saved value.
  const [draftThresholds, setDraftThresholds] = useState(() => ({
    ...getStoredThresholdsSnapshot(),
    is_open_role_visible: true,
  }));
  const [thresholdsStatus, setThresholdsStatus] = useState(ThresholdsStatus.LOADING);

  const updateDraftField = (field) => (value) => {
    setDraftThresholds((previous) => ({ ...previous, [field]: value }));
  };

  useEffect(() => {
    const loadThresholds = async () => {
      try {
        const fetchedThresholds = await getTalentMobilityThresholds();
        setDraftThresholds(fetchedThresholds);
        setStoredThresholds(toThresholdsPatch(fetchedThresholds));
        setStoredNotificationsEnabled(fetchedThresholds.matchNotificationsEnabled);
      } catch (error) {
        // Leave the form showing the store's last-known values (already
        // seeded above) instead of blocking the page on a failed fetch.
        SbNotification.error(
          "Unable to load thresholds",
          error?.response?.data?.message || "Showing your last saved values instead."
        );
      } finally {
        setThresholdsStatus(ThresholdsStatus.IDLE);
      }
    };
    loadThresholds();
  }, [setStoredThresholds, setStoredNotificationsEnabled]);

  const handleSave = async () => {
    const {
      matchGreenThreshold,
      matchRedThreshold,
      successionReadyThreshold,
      successionOneYearThreshold,
      matchNotificationsEnabled,
    } = draftThresholds;

    if (matchRedThreshold >= matchGreenThreshold) {
      SbNotification.warning("Invalid thresholds", "The red threshold must be lower than the green threshold.");
      return;
    }
    if (successionOneYearThreshold >= successionReadyThreshold) {
      SbNotification.warning("Invalid thresholds", "The \"Ready in 1 Year\" threshold must be lower than \"Ready Now\".");
      return;
    }

    setThresholdsStatus(ThresholdsStatus.SAVING);
    try {
      await updateTalentMobilityThresholds(draftThresholds);
      setStoredThresholds(toThresholdsPatch(draftThresholds));
      setStoredNotificationsEnabled(matchNotificationsEnabled);
      SbNotification.success("Saved", "Talent Mobility thresholds updated.");
    } catch (error) {
      SbNotification.error(
        "Unable to save thresholds",
        error?.response?.data?.message || "Please try again in a moment."
      );
    } finally {
      setThresholdsStatus(ThresholdsStatus.IDLE);
    }
  };

  return (
    <SbSpin spinning={thresholdsStatus === ThresholdsStatus.LOADING}>
      <PageWrapperStyled>
        <HeaderRowStyled>
          <BackButtonStyled onClick={() => navigate("/admin-setup")} aria-label="Back to Admin Setup">
            <ArrowLeftOutlined />
          </BackButtonStyled>
          <div>
            <h3 style={{ margin: 0 }}>Talent Mobility — Thresholds</h3>
            <p style={{ margin: 0, fontSize: 12.5, color: "#5A6A7A" }}>
              Controls the match/SRI score banding (applies to SSI, SMI, and SRI) and the Succession Score across Talent Mobility.
            </p>
          </div>
        </HeaderRowStyled>

        <SectionHeadingStyled>Match Thresholds</SectionHeadingStyled>
        <SettingsCardStyled>
          <FieldRowStyled>
            <div>
              <div className="label">Green threshold (%)</div>
              <div className="desc">Match/SRI scores at or above this render green.</div>
            </div>
            <InputNumber min={0} max={100} value={draftThresholds.matchGreenThreshold} onChange={updateDraftField("matchGreenThreshold")} />
          </FieldRowStyled>
          <FieldRowStyled>
            <div>
              <div className="label">Red threshold (%)</div>
              <div className="desc">Scores below this always render red. Anything between the two renders amber.</div>
            </div>
            <InputNumber min={0} max={100} value={draftThresholds.matchRedThreshold} onChange={updateDraftField("matchRedThreshold")} />
          </FieldRowStyled>
          <FieldRowStyled>
            <div>
              <div className="label">Enable Match Notifications</div>
              <div className="desc">When on, shortlisting a candidate notifies only the employee. When off, shortlisting is always silent.</div>
            </div>
            <SbSwitch checked={draftThresholds.matchNotificationsEnabled} onChange={updateDraftField("matchNotificationsEnabled")} />
          </FieldRowStyled>
          <FieldRowStyled>
            <div>
              <div className="label">Open Role Visibility</div>
              <div className="desc">When on, open roles are visible across Talent Mobility. When off, they are hidden.</div>
            </div>
            <SbSwitch checked={Boolean(draftThresholds.is_open_role_visible)} onChange={updateDraftField("is_open_role_visible")} />
          </FieldRowStyled>
        </SettingsCardStyled>

        <SectionHeadingStyled>Succession Threshold</SectionHeadingStyled>
        <SettingsCardStyled>
          <FieldRowStyled>
            <div>
              <div className="label">Ready Now threshold (%)</div>
              <div className="desc">SRI at or above this marks a successor "Ready for Next Role: Yes" and counts as "Ready Now" in the Succession Score (Engine D).</div>
            </div>
            <InputNumber min={0} max={100} value={draftThresholds.successionReadyThreshold} onChange={updateDraftField("successionReadyThreshold")} />
          </FieldRowStyled>
          <FieldRowStyled>
            <div>
              <div className="label">Ready in 1 Year threshold (%)</div>
              <div className="desc">SRI at or above this (but below Ready Now) marks a successor as ready within roughly a year, for the Succession Score (Engine D).</div>
            </div>
            <InputNumber min={0} max={100} value={draftThresholds.successionOneYearThreshold} onChange={updateDraftField("successionOneYearThreshold")} />
          </FieldRowStyled>
        </SettingsCardStyled>

        <Permission resource="talent_mobility_admin" actions={["edit"]}>
          <SbButton
            buttonType="primary"
            style={{ marginTop: 16 }}
            onClick={handleSave}
            loading={thresholdsStatus === ThresholdsStatus.SAVING}
            disabled={thresholdsStatus !== ThresholdsStatus.IDLE}
          >
            Save Thresholds
          </SbButton>
        </Permission>
      </PageWrapperStyled>
    </SbSpin>
  );
};

export default TalentMobilitySettings;
